import { Plugin, Chart as ChartJS } from 'chart.js';

/**
 * Shared OHLC candlestick rendering for Chart.js `<Chart type="bar">` charts.
 *
 * Chart.js has no built-in candlestick chart type, so both `PatternDetail`
 * (the main per-pattern price chart) and `MatchCandlestickChart` (the Query
 * Matcher match-card candlestick view) render an invisible bar dataset
 * carrying an `o`/`h`/`l`/`c` payload per point (`candleData`) and let this
 * `afterDatasetsDraw` plugin paint wicks + bodies directly on the canvas —
 * which also means OHLC values flow through Chart.js's normal scales/tooltip
 * machinery "for free" (axis ranges, hover detection, index-mode tooltips).
 *
 * Originally defined inline in `PatternDetail`; extracted here so the match
 * cards' candlestick view can reuse the exact same renderer/color convention
 * rather than re-deriving it (or falling back to a separate pure-SVG one).
 */

export interface OHLCPoint { o: number | null; h: number | null; l: number | null; c: number | null; }
export interface CandleDataset { candleData?: OHLCPoint[]; data: number[]; }

export const candlestickPlugin: Plugin = {
  id: 'candlestickRenderer',
  afterDatasetsDraw(chart: ChartJS) {
    const ctx = chart.ctx;
    const xScale = chart.scales['x'];
    const yScale = chart.scales['y'];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!xScale || !yScale) return;
    const meta = chart.getDatasetMeta(0);
    const dataset = chart.data.datasets[0] as unknown as CandleDataset;

    meta.data.forEach((bar, i) => {
      const candle = dataset.candleData?.[i];
      if (!candle) return;
      const { o, h, l, c } = candle;
      if (o === null || h === null || l === null || c === null) return;

      const xCenter = bar.x;
      const pixelWidth = xScale.getPixelForValue(1) - xScale.getPixelForValue(0);
      const bodyWidth = Math.max(Math.floor(pixelWidth * 0.6), 4);

      const yOpen  = yScale.getPixelForValue(o);
      const yClose = yScale.getPixelForValue(c);
      const yHigh  = yScale.getPixelForValue(h);
      const yLow   = yScale.getPixelForValue(l);

      const isBullish = c >= o;
      const bodyColor = isBullish ? '#26a69a' : '#ef5350';
      const wickColor = isBullish ? '#1a7068' : '#b33c38';

      // Wick
      ctx.beginPath();
      ctx.strokeStyle = wickColor;
      ctx.lineWidth = 1;
      ctx.moveTo(xCenter, yHigh);
      ctx.lineTo(xCenter, yLow);
      ctx.stroke();

      // Body
      const bodyTop = Math.min(yOpen, yClose);
      const bodyHeight = Math.max(Math.abs(yClose - yOpen), 1);
      ctx.fillStyle = bodyColor;
      ctx.fillRect(xCenter - bodyWidth / 2, bodyTop, bodyWidth, bodyHeight);
      ctx.strokeStyle = wickColor;
      ctx.lineWidth = 1;
      ctx.strokeRect(xCenter - bodyWidth / 2, bodyTop, bodyWidth, bodyHeight);
    });
  },
};

export default candlestickPlugin;
